"use client";

import React from "react";
import { MessageSquare as MessageSquareCheck, ClipboardList, Hammer, ArrowRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface PhilosophyPillar {
  icon: React.ComponentType<{
    className?: string;
  }>;
  title: string;
  subtitle: string;
  description: string;
}
const PILLARS: PhilosophyPillar[] = [{
  icon: MessageSquareCheck,
  title: "Direct client communication",
  subtitle: "No guesswork or surprises",
  description: "We provide scheduled weekly updates, direct access to your site lead, and real-time project schedules so you always know what is happening in your home."
}, {
  icon: ClipboardList,
  title: "Disciplined project planning",
  subtitle: "Engineered from day one",
  description: "Before demo begins, we finalize every blueprint, permit with DeKalb County and City of Brookhaven inspectors, and procure all finish materials to prevent delays."
}, {
  icon: Hammer,
  title: "Uncompromising execution",
  subtitle: "Built for generational longevity",
  description: "Experienced tradespeople handle your structural framing, custom cabinetry, plumbing, and fine tilework with stringent quality control at each milestone."
}];
export default function ProcessPhilosophy() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="processphilosophy" data-nav="light" ref={ref} className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header grid */}
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-16 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7">
            <span className="text-xs font-semibold uppercase tracking-wider text-primary mb-3 block">
              Our Renovation Philosophy
            </span>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-foreground text-balance">
              Predictable execution for thoughtful Brookhaven homeowners
            </h1>
          </div>
          <div className="lg:col-span-5 flex flex-col justify-end">
            <p className="text-base sm:text-lg text-foreground/80 leading-relaxed font-default mb-6">
              Major residential renovations should bring confidence, not stress. We build our client relationships on clear municipal compliance, open dialogue, and dependable site management across Brookhaven and DeKalb County.
            </p>
            <div>
              <Button asChild variant="outline" size="default" className="group">
                <Link to="/ContactRequestAnEstimatePage">
                  Request a consultation
                  <ArrowRight className="size-4 ml-1.5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Visual composition + philosophy pillars */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-stretch">
          {/* Photo display */}
          <div className={`lg:col-span-6 flex flex-col justify-between gap-4 transition-all duration-700 delay-100 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative rounded-xl overflow-hidden shadow-md border border-border flex-1 min-h-[320px] bg-muted">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_renovated_kitchen_brookhaven.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_renovated_kitchen_brookhaven.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_renovated_kitchen_brookhaven.png-wvc-sizes" alt="Custom Brookhaven kitchen renovation showing marble island and bespoke cabinets" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-4 left-4 right-4 text-white">
                <p className="text-xs font-medium uppercase tracking-wider text-white/80">Completed Project</p>
                <p className="text-sm font-serif font-medium">Historic Brookhaven Kitchen & Main Level Remodel</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="relative rounded-lg overflow-hidden shadow-xs border border-border h-36 sm:h-44 bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_planning_consultation.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_planning_consultation.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_planning_consultation.webp-wvc-sizes" alt="Lead designer reviewing renovation plans and architectural blueprints with homeowners" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
              </div>
              <div className="relative rounded-lg overflow-hidden shadow-xs border border-border h-36 sm:h-44 bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/craftsman_woodworking_trim_execution.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/craftsman_woodworking_trim_execution.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/craftsman_woodworking_trim_execution.png-wvc-sizes" alt="Carpentry craftsman installing precision millwork during residential renovation" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
              </div>
            </div>
          </div>

          {/* Core process pillars */}
          <div className="lg:col-span-6 flex flex-col justify-between gap-4">
            {PILLARS.map((pillar, i) => {
            const Icon = pillar.icon;
            return <div key={pillar.title} data-index={i} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
              transitionDelay: `${150 + i * 100}ms`
            }}>
                  <Card className="border border-border/80 bg-card shadow-xs hover:shadow-md transition-shadow h-full">
                    <CardHeader className="flex flex-row items-start gap-4 space-y-0 pb-2">
                      <div className="size-11 rounded-lg bg-primary/15 text-primary-foreground flex items-center justify-center shrink-0">
                        <Icon className="size-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <span className="text-xs uppercase font-semibold text-primary block tracking-wider">
                          0{i + 1} &mdash; {pillar.subtitle}
                        </span>
                        <CardTitle className="font-serif text-xl sm:text-2xl font-medium text-card-foreground mt-0.5">
                          {pillar.title}
                        </CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-2 text-foreground/80 text-sm sm:text-base leading-relaxed">
                      {pillar.description}
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}